import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { formatDisplayEmail } from '../utils';
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Text,
  Group,
  Avatar,
  Alert,
  ScrollArea,
  Badge,
  Select,
  Checkbox,
  Divider,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconMail, IconCheck, IconSearch, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { sendInvitation, searchUserByEmailInfix } from '@mygpt/app';
import { useGPTStore } from '@/store';
import { useAdminStore } from '@/store/admin';
import { usePermissionData } from '../hooks/usePermissionData';
import { usePermissionControl } from '../hooks/usePermissionControl';

interface InviteMemberModalProps {
  opened: boolean;
  onClose: () => void;
  onInviteSent?: () => void;
}

// OGS 用户搜索结果类型
interface OgsUser {
  id: string;
  uuid: string;
  name: string;
  email: string;
  actived: boolean;
  enabled: boolean;
  defaultCompanyId: string;
  userCompanyActived: boolean;
  userCompanyEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// 最少搜索字符数
const MIN_SEARCH_CHARS = 3;
// 搜索结果数量上限
const SEARCH_RECORD_COUNT = 10;

// 获取所有部门列表
const getAllDepartments = (nodes: any[], path: string[] = []): Array<{ path: string; id: string; isLeaf: boolean }> => {
  let departments: Array<{ path: string; id: string; isLeaf: boolean }> = [];
  for (const node of nodes) {
    const currentPath = node.id === 'root' ? [node.name] : [...path, node.name];
    const pathString = currentPath.join(' > ');
    const isLeaf = !node.children || node.children.length === 0;
    departments.push({ path: pathString, id: node.id, isLeaf });
    if (node.children && node.children.length > 0) {
      departments = departments.concat(getAllDepartments(node.children, currentPath));
    }
  }
  return departments;
};

// 过滤可选择的部门（与AddUserDialog保持一致）
const getFilteredDepartments = (
  allDepartments: Array<{ path: string; id: string; isLeaf: boolean }>,
  permissionControl: any
) => {
  // 公司所有者和管理员可以选择所有部门
  if (permissionControl.isOwner || permissionControl.isAdmin) {
    return allDepartments;
  }

  // 部门管理员只能选择自己所属部门及其子部门
  if (permissionControl.currentUserRole === 'ROLE_ADMIN_DEPARTMENT') {
    const currentUserPath = permissionControl.currentUser?.departmentPath;
    if (!currentUserPath) return [];

    return allDepartments.filter((dept) => {
      // 根节点不可选择
      if (dept.id === 'root') return false;

      // 检查部门是否是当前用户部门或其子部门
      return dept.path.startsWith(currentUserPath);
    });
  }

  return [];
};

// 根据角色 code 返回多语言显示名，避免使用后端返回的 role.name 中文原文
const getPermissionText = (roleCode: string, t: any) => {
  switch (roleCode) {
    case 'ROLE_COMPANY_OWNER':
      return t('ROLE_COMPANY_OWNER') || 'Company Owner';
    case 'ROLE_ADMIN':
      return t('ROLE_ADMIN') || 'Administrator';
    case 'ROLE_ADMIN_DEPARTMENT':
      return t('ROLE_ADMIN_DEPARTMENT') || 'Department Admin';
    case 'ROLE_EDITOR':
      return t('ROLE_EDITOR') || 'Editor';
    case 'ROLE_VIEWER':
      return t('ROLE_VIEWER') || 'Viewer';
    default:
      return t('ROLE_VIEWER') || 'Viewer';
  }
};

/**
 * Cloud版邀请成员弹窗
 * 交互流程：先选部门/角色 → 搜索用户 → 勾选 → 批量邀请或单独发送
 */
export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ opened, onClose, onInviteSent }) => {
  const { t } = useTranslation();
  const { userInfo } = useGPTStore();
  const { companyId, userOgsId } = useAdminStore();

  // 部门和角色（顶部统一选择）
  const [selectedDepartmentPath, setSelectedDepartmentPath] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [deptError, setDeptError] = useState('');
  const [roleError, setRoleError] = useState('');

  // 搜索
  const [email, setEmail] = useState('');
  const [searchResults, setSearchResults] = useState<OgsUser[]>([]);
  const [searchError, setSearchError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // 选中用户（批量邀请）
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // 邀请状态：单个邀请记录正在邀请的用户ID，批量邀请用 'batch' 标记
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const isBatchInviting = invitingId === 'batch';
  const isAnyInviting = invitingId !== null;

  const { organizationData, loadAssignableRoles } = usePermissionData();
  const permissionControl = usePermissionControl();

  // 可分配角色列表
  const [assignableRoles, setAssignableRoles] = useState<any[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const rolesLoadedRef = useRef(false);

  // 加载角色列表（仅在弹窗首次打开时加载一次）
  useEffect(() => {
    if (opened && !rolesLoadedRef.current) {
      rolesLoadedRef.current = true;
      const loadRoles = async () => {
        setLoadingRoles(true);
        try {
          const roles = await loadAssignableRoles();
          setAssignableRoles(roles);
        } catch (error) {
          console.error('加载可分配角色失败:', error);
          setAssignableRoles([]);
        } finally {
          setLoadingRoles(false);
        }
      };
      loadRoles();
    }
    if (!opened) {
      rolesLoadedRef.current = false;
    }
  }, [opened]);

  // 部门选项（根据权限过滤）
  const allDepartments = useMemo(() => getAllDepartments(organizationData), [organizationData]);
  const filteredDepartments = useMemo(
    () => getFilteredDepartments(allDepartments, permissionControl),
    [allDepartments, permissionControl]
  );
  const departmentOptions = useMemo(() => {
    return filteredDepartments.map((d) => ({ value: d.path, label: d.path }));
  }, [filteredDepartments]);

  // 角色选项
  const roleOptions = useMemo(() => {
    return assignableRoles.map((role) => ({
      value: role.id.toString(),
      label: getPermissionText(role.code, t),
    }));
  }, [assignableRoles, t]);

  // 判断用户是否已有企业
  const userHasCompany = (user: OgsUser) => {
    return user.defaultCompanyId && user.defaultCompanyId !== '0' && user.userCompanyActived;
  };

  // 可邀请的用户（排除已有企业的）
  const invitableUsers = useMemo(() => {
    return searchResults.filter((u) => !userHasCompany(u));
  }, [searchResults]);

  // 当前选中的可邀请用户数
  const selectedInvitableCount = useMemo(() => {
    return invitableUsers.filter((u) => selectedUserIds.has(u.id)).length;
  }, [invitableUsers, selectedUserIds]);

  // 全选/取消全选
  const allInvitableSelected = invitableUsers.length > 0 && invitableUsers.every((u) => selectedUserIds.has(u.id));

  const handleToggleSelectAll = () => {
    if (allInvitableSelected) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(invitableUsers.map((u) => u.id)));
    }
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // 验证部门和角色
  const validateDeptAndRole = (): boolean => {
    let hasError = false;
    if (!selectedDepartmentPath) {
      setDeptError(t('adminConsole.invite.departmentRequired', '请选择部门'));
      hasError = true;
    }
    if (!selectedRoleId) {
      setRoleError(t('adminConsole.invite.roleRequired', '请选择角色'));
      hasError = true;
    }
    return !hasError;
  };

  const handleSearch = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setSearchError(t('adminConsole.invite.emailRequired', '请输入邮箱地址'));
      return;
    }
    if (trimmed.length < MIN_SEARCH_CHARS) {
      setSearchError(t('adminConsole.invite.minSearchChars', '请至少输入{{count}}个字符', { count: MIN_SEARCH_CHARS }));
      return;
    }

    setSearchError('');
    setSearchResults([]);
    setSelectedUserIds(new Set());
    setSearchLoading(true);

    try {
      const response = await searchUserByEmailInfix(trimmed, SEARCH_RECORD_COUNT);
      const resData = response?.data ?? response;
      const users: OgsUser[] = resData?.users || [];

      if (users.length === 0) {
        setSearchError(t('adminConsole.invite.userNotFound', '未找到该用户，请确认邮箱地址'));
        return;
      }

      // 过滤掉未激活的用户
      const activeUsers = users.filter((u) => u.actived && u.enabled);
      if (activeUsers.length === 0) {
        setSearchError(t('adminConsole.invite.userNotFound', '未找到该用户，请确认邮箱地址'));
        return;
      }

      setSearchResults(activeUsers);
    } catch (err: any) {
      console.error('搜索用户失败:', err);
      setSearchError(t('adminConsole.invite.searchFailed', '搜索失败，请重试'));
    } finally {
      setSearchLoading(false);
    }
  }, [email, t]);

  // 发送单个邀请
  const doInviteUser = async (user: OgsUser, departmentId: string | undefined) => {
    await sendInvitation({
      user_id: user.uuid,
      user_ogs_id: user.id,
      email: user.email,
      inviter_id: userInfo?.user_id || '',
      inviter_ogs_id: userOgsId || '',
      inviter_name: userInfo?.name || userInfo?.email || '',
      company_id: companyId!,
      department_id: departmentId,
      sys_role_id: selectedRoleId || undefined,
    });
  };

  // 单个邀请
  const handleInviteSingle = async (user: OgsUser) => {
    if (!companyId || !validateDeptAndRole()) return;

    const department = filteredDepartments.find((d) => d.path === selectedDepartmentPath);
    const departmentId = department && department.id !== 'root' ? department.id : undefined;

    setInvitingId(user.id);
    try {
      await doInviteUser(user, departmentId);
      showNotification({
        color: 'green',
        message: t('adminConsole.invite.success', '邀请已发送，对方将在消息中心收到通知'),
      });
      setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      setSelectedUserIds((prev) => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
      onInviteSent?.();
    } catch (err: any) {
      console.error('发送邀请失败:', err);
    } finally {
      setInvitingId(null);
    }
  };

  // 批量邀请（一次性发送数组）
  const handleInviteBatch = async () => {
    if (!companyId || !validateDeptAndRole()) return;

    const usersToInvite = invitableUsers.filter((u) => selectedUserIds.has(u.id));
    if (usersToInvite.length === 0) return;

    const department = filteredDepartments.find((d) => d.path === selectedDepartmentPath);
    const departmentId = department && department.id !== 'root' ? department.id : undefined;

    setInvitingId('batch');

    try {
      const invitationParams = usersToInvite.map((user) => {
        return {
          user_id: user.uuid,
          user_ogs_id: user.id,
          email: user.email,
          inviter_id: userInfo?.user_id || '',
          inviter_ogs_id: userOgsId || '',
          inviter_name: userInfo?.name || userInfo?.email || '',
          company_id: companyId!,
          department_id: departmentId,
          sys_role_id: selectedRoleId || undefined,
        };
      });

      await sendInvitation(invitationParams);

      setSearchResults((prev) => prev.filter((u) => !selectedUserIds.has(u.id)));
      setSelectedUserIds(new Set());
      onInviteSent?.();

      showNotification({
        color: 'green',
        message: t('adminConsole.invite.batchSuccess', '已成功发送{{count}}条邀请', { count: usersToInvite.length }),
      });
    } catch (err: any) {
      console.error('批量邀请失败:', err);
    } finally {
      setInvitingId(null);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSearchResults([]);
    setSearchError('');
    setSelectedUserIds(new Set());
    setInvitingId(null);
    setSelectedDepartmentPath(null);
    setSelectedRoleId(null);
    setDeptError('');
    setRoleError('');
    setSearchLoading(false);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // 忽略输入法组字过程中的回车（如中文/日文输入法确认候选词）
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !searchLoading) {
      handleSearch();
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t('adminConsole.invite.title', '邀请成员')} centered size="lg">
      <Stack>
        <Text size="sm" c="dimmed">
          {t('adminConsole.invite.description', '选择部门和角色，然后搜索用户发送邀请')}
        </Text>

        {/* 第一步：选择部门和角色 */}
        <Group grow>
          <Select
            label={t('adminConsole.invite.selectDepartment', '选择部门')}
            placeholder={t('adminConsole.invite.selectDepartment', '请选择部门')}
            data={departmentOptions}
            searchable
            required
            value={selectedDepartmentPath}
            onChange={(value) => {
              setSelectedDepartmentPath(value);
              setDeptError('');
            }}
            error={deptError || undefined}
          />
          <Select
            label={t('adminConsole.invite.selectRole', '选择角色')}
            placeholder={
              loadingRoles ? t('common.loading', '加载中...') : t('adminConsole.invite.selectRole', '请选择角色')
            }
            data={roleOptions}
            disabled={loadingRoles}
            required
            value={selectedRoleId}
            onChange={(value) => {
              setSelectedRoleId(value);
              setRoleError('');
            }}
            error={roleError || undefined}
          />
        </Group>

        <Divider />

        {/* 第二步：搜索用户 */}
        <Group align="flex-end">
          <TextInput
            placeholder={t('adminConsole.invite.emailPlaceholder', '请输入要邀请的用户邮箱')}
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value);
              setSearchError('');
            }}
            onKeyDown={handleKeyPress}
            leftSection={<IconSearch size={16} />}
            error={searchError && searchResults.length === 0 ? searchError : undefined}
            style={{ flex: 1 }}
          />
          <Button onClick={handleSearch} loading={searchLoading}>
            {t('adminConsole.invite.search', '搜索')}
          </Button>
        </Group>

        {/* 搜索结果列表 */}
        {searchResults.length > 0 && (
          <>
            {/* 全选 + 批量邀请操作栏 */}
            {invitableUsers.length > 1 && (
              <Group justify="space-between">
                <Checkbox
                  label={t('adminConsole.invite.selectAll', '全选')}
                  checked={allInvitableSelected}
                  indeterminate={selectedInvitableCount > 0 && !allInvitableSelected}
                  onChange={handleToggleSelectAll}
                />
                {selectedInvitableCount > 0 && (
                  <Button
                    size="xs"
                    color="violet"
                    leftSection={<IconUsers size={14} />}
                    onClick={handleInviteBatch}
                    loading={isBatchInviting}
                    disabled={isAnyInviting && !isBatchInviting}
                  >
                    {t('adminConsole.invite.batchInvite', '批量邀请 ({{count}})', {
                      count: selectedInvitableCount,
                    })}
                  </Button>
                )}
              </Group>
            )}

            <ScrollArea.Autosize mah={300}>
              <Stack gap="xs">
                {searchResults.map((user) => {
                  const hasCompany = userHasCompany(user);
                  const isSelected = selectedUserIds.has(user.id);

                  return (
                    <div key={user.id} className="px-3 py-2 rounded-md border border-gray-200 flex items-center gap-3">
                      {/* 勾选框（仅可邀请用户） */}
                      {!hasCompany && invitableUsers.length > 1 && (
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleUser(user.id)}
                          styles={{ input: { cursor: 'pointer' } }}
                        />
                      )}

                      {/* 用户信息 */}
                      <Group wrap="nowrap" gap="sm" style={{ flex: 1, minWidth: 0 }}>
                        <Avatar radius="xl" size="sm">
                          {user.name?.charAt(0)?.toUpperCase() ||
                            formatDisplayEmail(user.email)?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <div style={{ minWidth: 0 }}>
                          <Text size="sm" fw={500} lineClamp={1}>
                            {user.name || formatDisplayEmail(user.email)}
                          </Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {formatDisplayEmail(user.email)}
                          </Text>
                        </div>
                      </Group>

                      {/* 操作区 */}
                      {hasCompany ? (
                        <Badge color="yellow" variant="light" size="sm" style={{ flexShrink: 0 }}>
                          {t('adminConsole.invite.alreadyHasOrg', '该用户已有组织')}
                        </Badge>
                      ) : (
                        <Button
                          size="xs"
                          variant="light"
                          color="violet"
                          onClick={() => handleInviteSingle(user)}
                          loading={invitingId === user.id}
                          disabled={isAnyInviting && invitingId !== user.id}
                          leftSection={<IconMail size={14} />}
                          style={{ flexShrink: 0 }}
                        >
                          {t('adminConsole.invite.send', '邀请')}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          </>
        )}

        {searchError && searchResults.length > 0 && (
          <Text c="red" size="sm">
            {searchError}
          </Text>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            {t('common.cancel', '取消')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default InviteMemberModal;
